import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import CloudflareImage from './ui/cloudflare-image';
import { getSettings } from '../services/publicService';

const BannerContainer = styled.div`
  container-type: inline-size;
  margin: 0 auto;
`;

const BannerWrapper = styled.div`
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
  width: 100%;
  max-width: 90vw;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  
  @media (min-width: 768px) {
    margin-top: 2.5rem;
    margin-bottom: 2.5rem;
  }
  
  @media (min-width: 1536px) {
    max-width: 1600px;
  }
`;

const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  border-radius: 0.75rem;
  overflow: hidden;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  margin: 0 auto;
  border-radius: 0.75rem;
`;

const ImageSlide = styled.div`
  width: 100%;
  height: auto;
  display: ${props => props.$active ? 'block' : 'none'};
  opacity: ${props => props.$active ? 1 : 0};
  transition: opacity 0.5s ease-in-out;
`;

const SlideImage = styled(CloudflareImage)`
  width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: 0.75rem;
  display: block;
  
  &.mobile-only {
    display: block;
    @media (min-width: 768px) {
      display: none;
    }
  }
  
  &.desktop-only {
    display: none;
    @media (min-width: 768px) {
      display: block;
    }
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  ${props => props.direction === 'left' ? 'left: 10px;' : 'right: 10px;'}
  
  @media (min-width: 768px) {
    width: 50px;
    height: 50px;
  }
`;

const SlideIndicators = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
`;

const Indicator = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.8);
  }
`;


const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 1.25rem;
  margin-left: auto;
  margin-right: auto;
  max-width: 90vw;
  width: 100%;
  justify-content: space-between;
  
  @media (min-width: 1280px) {
    margin-top: 3rem;
  }
`;

const DropButton = styled.div``;

const SeasonButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  background-color: #000;
  color: white;
  padding: 0.75rem 2.5rem;
  border-radius: 0.375rem;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  text-decoration: none;
  
  &:hover {
    transform: scale(1.02);
    background-color: #111;
  }
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const DropText = styled.div`
  margin-top: 1rem;
  
  p {
    color: #dc2626;
    font-size: 1rem;
  }
`;

const HomeBanner = () => {
  const [bannerItems, setBannerItems] = useState([]);
  const [autoSlideInterval, setAutoSlideInterval] = useState(5000);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBannerSettings = async () => {
      try {
        const response = await getSettings();
        if (response.success && response.settings?.homeBanner) {
          const { items, autoSlideInterval: interval } = response.settings.homeBanner;
          setBannerItems(items || []);
          setAutoSlideInterval(interval || 5000);
        }
      } catch (error) {
        console.error('Error fetching banner settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBannerSettings();
  }, []);

  // Auto-slide for images
  useEffect(() => {
    if (bannerItems.length > 1 && bannerItems.every(item => item.type === 'image')) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % bannerItems.length);
      }, autoSlideInterval);

      return () => clearInterval(interval);
    }
  }, [bannerItems, autoSlideInterval]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % bannerItems.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + bannerItems.length) % bannerItems.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  if (loading) {
    return null; // Or a loading spinner
  }

  if (bannerItems.length === 0) {
    return null; // No banner items configured
  }

  // If only one item and it's a video, show video
  const firstItem = bannerItems[0];
  const isVideoOnly = bannerItems.length === 1 && firstItem.type === 'video';
  const isImageSlideshow = bannerItems.length > 0 && bannerItems.every(item => item.type === 'image');

  return (
    <>
      <BannerContainer>
        <BannerWrapper>
          <MediaContainer>
            {isVideoOnly ? (
              <>
                <Video autoPlay loop muted playsInline className="hidden md:block">
                  <source src={firstItem.mobileUrl || firstItem.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </Video>
                <Video autoPlay loop muted playsInline className="block md:hidden">
                  <source src={firstItem.mobileUrl || firstItem.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </Video>
              </>
            ) : isImageSlideshow ? (
              <>
                {bannerItems.map((item, index) => (
                  <ImageSlide key={item._id || index} $active={index === currentSlide}>
                    {item.mobileUrl ? (
                      <>
                        <SlideImage 
                          src={item.mobileUrl} 
                          alt={`Banner slide ${index + 1}`}
                          className="mobile-only"
                        />
                        <SlideImage 
                          src={item.url} 
                          alt={`Banner slide ${index + 1}`}
                          className="desktop-only"
                        />
                      </>
                    ) : (
                      <SlideImage 
                        src={item.url} 
                        alt={`Banner slide ${index + 1}`}
                      />
                    )}
                  </ImageSlide>
                ))}
                {bannerItems.length > 1 && (
                  <>
                    <ArrowButton direction="left" onClick={prevSlide}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 18l-6-6 6-6"/>
                      </svg>
                    </ArrowButton>
                    <ArrowButton direction="right" onClick={nextSlide}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                    </ArrowButton>
                    <SlideIndicators>
                      {bannerItems.map((_, index) => (
                        <Indicator
                          key={index}
                          $active={index === currentSlide}
                          onClick={() => goToSlide(index)}
                        />
                      ))}
                    </SlideIndicators>
                  </>
                )}
              </>
            ) : null}
            
          </MediaContainer>
        </BannerWrapper>
        
        <ButtonRow>
          <DropButton>
            <SeasonButton to="/shop/shop-by-season">
              <span>Shop Now</span>
            </SeasonButton>
            <DropText>
              <p>Zone is live!</p>
            </DropText>
          </DropButton>
        </ButtonRow>
      </BannerContainer>
    </>
  );
};

export default HomeBanner;
